import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { RecentSearches } from '@theme/recent-searches';
import { DialogCloseEvent, DialogClosingEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogClosingEvent.eventName, this.#handleDialogClosing, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // While the panel is open the header's gutter and height can change on
    // resize (the header swaps layouts at its breakpoint), so re-measure to keep
    // the panel docked and its content aligned with the logo.
    window.addEventListener('resize', this.#handleResize, { signal });

    this.refs.form?.addEventListener('submit', () => this.#recordSearch(), { signal });

    if (this.#hasEmptyStateData) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Whether there is client-side data (recently viewed or recent searches)
   * that needs to be injected into the empty state.
   * @returns {boolean}
   */
  get #hasEmptyStateData() {
    return RecentlyViewed.getProducts().length > 0 || RecentSearches.getTerms().length > 0;
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input,
   * and records the current term as a recent search when a result/link is followed.
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    if (target.closest('[data-clear-recent-searches]')) {
      event.stopPropagation();
      this.#clearRecentSearches();
      return;
    }

    if (target.closest('[data-clear-recently-viewed]')) {
      event.stopPropagation();
      this.#clearRecentlyViewed();
      return;
    }

    // Recent/Popular search terms run the query inside the modal instead of
    // navigating to the full search page.
    const termLink = target.closest('[data-search-term]');
    if (termLink instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      const term = termLink.dataset.searchTerm || termLink.textContent?.trim() || '';
      this.#runTermSearch(term);
      return;
    }

    const link = target.closest('a');
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      link ||
      target.closest('input');

    // A real result/link was followed — remember what was typed.
    if (link && !link.closest('[data-search-terms-group]')) {
      this.#recordSearch();
    }

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  /**
   * Persists the current (non-empty) input value to recent searches.
   */
  #recordSearch() {
    const term = this.refs.searchInput?.value.trim();
    if (term) RecentSearches.addTerm(term);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Fired when the closing animation starts: unpin the header immediately so
   * it (and the announcement bar) restore in sync with the panel fade-out
   * instead of flashing the pinned white header afterwards.
   */
  #handleDialogClosing = () => {
    document.documentElement.removeAttribute('data-search-open');
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    document.documentElement.removeAttribute('data-search-open');
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    document.documentElement.setAttribute('data-search-open', '');
    this.#syncPanelTop();
    if (!this.#emptyStateLoaded && this.#hasEmptyStateData) {
      this.#loadEmptyState();
    }
  };

  /**
   * Re-syncs the panel position and gutter on resize, but only while the panel
   * is open (the CSS variables are irrelevant when it is closed). Debounced to
   * avoid measuring on every resize frame.
   */
  #handleResize = debounce(() => {
    if (document.documentElement.hasAttribute('data-search-open')) {
      this.#syncPanelTop();
    }
  }, 100);

  /**
   * Measures the header's height and exposes it as a CSS variable so the desktop
   * search panel can dock directly beneath the header (matching Figma). Measured
   * after the header is pinned (data-search-open) so the value is stable.
   */
  #syncPanelTop() {
    requestAnimationFrame(() => {
      const root = document.documentElement;

      // The header is pinned to the top (top: 0) while search is open, so the
      // panel docks directly beneath the header — its own height, regardless of
      // the announcement bar or scroll position.
      const header = document.querySelector('#header-component');
      const headerHeight = header instanceof HTMLElement ? header.getBoundingClientRect().height : 0;
      root.style.setProperty('--search-panel-top', `${Math.max(0, Math.round(headerHeight))}px`);

      // Align search content with the header's page gutter (so the input and
      // column headings line up with the logo / cart). Measure the logo's own
      // left edge rather than the header columns container: the header swaps
      // between a padded flex layout (below 800px) and a grid-margin layout
      // (800px+), so the container's left edge does not track the logo. On
      // mobile we let the CSS fallback padding apply.
      const logo = document.querySelector('#header-component .header-logo');
      const gutter = logo instanceof HTMLElement ? Math.round(logo.getBoundingClientRect().left) : 0;
      if (window.innerWidth >= 750 && gutter > 0) {
        root.style.setProperty('--search-gutter', `${gutter}px`);
      } else {
        root.style.removeProperty('--search-gutter');
      }

      // Mobile: measure the sticky search input header so the scrollable results
      // area can start exactly beneath it.
      const searchHeader = this.querySelector('.predictive-search-form__header');
      const searchHeaderHeight =
        searchHeader instanceof HTMLElement ? Math.round(searchHeader.getBoundingClientRect().height) : 56;
      root.style.setProperty('--search-mobile-header-height', `${searchHeaderHeight}px`);
    });
  }

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__relevant-list, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(
          container.querySelectorAll(
            '[ref="resultsItems[]"], .predictive-search-results__relevant-item, .predictive-search-results__card'
          )
        );
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        this.#recordSearch();

        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Runs a search for a term (recent/popular suggestion) inside the modal,
   * without navigating to the full search results page.
   * @param {string} term - The search term.
   */
  #runTermSearch(term) {
    const trimmed = term.trim();
    if (!trimmed) return;

    const { searchInput } = this.refs;
    searchInput.value = trimmed;
    searchInput.focus();
    this.#showResetButton();
    this.#currentIndex = -1;
    this.#getSearchResults(trimmed);
    RecentSearches.addTerm(trimmed);
  }

  /**
   * Clears the recently viewed products and removes the column.
   */
  #clearRecentlyViewed() {
    RecentlyViewed.clearProducts();

    const mount = this.querySelector('[data-recently-viewed-mount]');
    if (mount instanceof HTMLElement) {
      mount.replaceChildren();
      mount.hidden = true;
    }
  }

  /**
   * Clears the stored recent search terms and removes the rendered section.
   */
  #clearRecentSearches() {
    RecentSearches.clearTerms();

    const mount = this.#recentSearchesMount;
    if (mount) {
      mount.replaceChildren();
      mount.hidden = true;
    }
  }

  /**
   * @returns {HTMLElement | null} The recent-searches mount point in the empty state.
   */
  get #recentSearchesMount() {
    return this.querySelector('[data-recent-searches-mount]');
  }

  /**
   * Renders the recent search terms (from localStorage) into the empty-state mount point.
   */
  #renderRecentSearches() {
    const mount = this.#recentSearchesMount;
    if (!mount) return;

    const terms = RecentSearches.getTerms();
    if (terms.length === 0) {
      mount.replaceChildren();
      mount.hidden = true;
      return;
    }

    const searchUrl = Theme.routes.search_url;
    const title = this.#translate(window.Theme?.translations?.recent_searches, 'Recent searches');

    const items = terms
      .map(
        (/** @type {string} */ term) =>
          `<li class="predictive-search-terms__item"><a class="predictive-search-terms__link" href="${searchUrl}?q=${encodeURIComponent(
            term
          )}" data-search-term="${this.#escapeHtml(term)}">${this.#escapeHtml(term)}</a></li>`
      )
      .join('');

    mount.innerHTML = `
      <div class="predictive-search-terms predictive-search-terms--recent" data-search-terms-group="recent">
        <h4 class="predictive-search-terms__title">${this.#escapeHtml(title)}</h4>
        <ul class="predictive-search-terms__list list-unstyled">${items}</ul>
      </div>`;
    mount.hidden = false;
  }

  /**
   * Returns a usable translation, falling back when the key is absent or unresolved.
   * @param {string | undefined} value - The translated string from Theme.translations.
   * @param {string} fallback - The fallback string.
   * @returns {string}
   */
  #translate(value, fallback) {
    if (!value || value.startsWith('Translation missing:')) return fallback;
    return value;
  }

  /**
   * @param {string} value - Raw string to escape for safe HTML insertion.
   * @returns {string}
   */
  #escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value;
    return div.innerHTML;
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    // The empty section renders the recently-viewed thumbnail column for id-based searches.
    return sectionRenderer.getSectionHTML('predictive-search-empty', false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();

      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const viewedColumn = parsedRecentlyViewedMarkup.querySelector('.predictive-search-viewed');
        const mount = parsedEmptySectionMarkup.querySelector('[data-recently-viewed-mount]');

        if (viewedColumn && mount) {
          viewedColumn.setAttribute('ref', 'recentlyViewedWrapper');
          mount.replaceChildren(viewedColumn);
          mount.removeAttribute('hidden');
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#renderRecentSearches();
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
